import React, { useState, useEffect } from 'react';
import { FileText, Check } from 'lucide-react';
import { Answer } from '../types';
import questionsData from '../data/questions.json';

interface Props {
  framework: string;
  role: string;
  onComplete: (answers: Answer[]) => void;
}

const QuestionsPage: React.FC<Props> = ({ framework, role, onComplete }) => {
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [currentPrinciple, setCurrentPrinciple] = useState<string>('');
  const [noteModal, setNoteModal] = useState<{ questionId: string; show: boolean }>({
    questionId: '',
    show: false,
  });
  const [tempNote, setTempNote] = useState('');

  const principles = questionsData.principles;
  const questions = questionsData.questions;

  useEffect(() => {
    if (principles.length > 0) {
      setCurrentPrinciple(principles[0].name);
    }
    
    // Initialize answers array
    const initialAnswers: Answer[] = questions.map(q => ({
      questionId: q.id,
      value: null,
      note: ''
    }));
    setAnswers(initialAnswers);
  }, []);

  const handleAnswer = (questionId: string, value: 'yes' | 'partially' | 'no') => {
    setAnswers(prev => 
      prev.map(a => 
        a.questionId === questionId ? { ...a, value } : a
      )
    );
  };

  const handleNote = (questionId: string) => {
    const answer = answers.find(a => a.questionId === questionId);
    setTempNote(answer?.note || '');
    setNoteModal({ questionId, show: true });
  };

  const saveNote = () => {
    setAnswers(prev =>
      prev.map(a =>
        a.questionId === noteModal.questionId ? { ...a, note: tempNote } : a
      )
    );
    setNoteModal({ questionId: '', show: false });
    setTempNote('');
  };

  const getAnsweredCount = (principleName: string) => {
    const principleQuestions = questions.filter(q => q.principle === principleName);
    return principleQuestions.filter(q => {
      const answer = answers.find(a => a.questionId === q.id);
      return answer?.value !== null;
    }).length;
  };

  const getTotalAnswered = () => {
    return answers.filter(a => a.value !== null).length;
  };

  const getProgress = () => {
    return Math.round((getTotalAnswered() / questions.length) * 100);
  };

  const canSubmit = () => {
    return answers.every(a => a.value !== null);
  };

  const currentQuestions = questions.filter(q => q.principle === currentPrinciple);
  const currentPrincipleData = principles.find(p => p.name === currentPrinciple);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <div className="flex">
        {/* Sidebar */}
        <div className="w-80 bg-white shadow-lg p-6 min-h-screen">
          <h2 className="text-2xl font-bold mb-6 gradient-text">Principles</h2>
          
          {principles.map(principle => {
            const answered = getAnsweredCount(principle.name);
            const total = questions.filter(q => q.principle === principle.name).length;
            const isCompleted = answered === total;
            const isActive = currentPrinciple === principle.name;

            return (
              <div
                key={principle.name}
                className={`sidebar-item ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                onClick={() => setCurrentPrinciple(principle.name)}
              >
                <span className="text-2xl">{principle.icon}</span>
                <div className="flex-1">
                  <div className="font-semibold">{principle.name}</div>
                  <div className="text-sm text-gray-500">
                    {answered}/{total} Completed
                  </div>
                </div>
                {isCompleted && <span className="checkmark">✓</span>}
              </div>
            );
          })}

          <div className="mt-8 p-4 bg-indigo-50 rounded-lg">
            <div className="text-sm font-semibold mb-2">Overall Progress</div>
            <div className="progress-bar mb-2">
              <div className="progress-fill" style={{ width: `${getProgress()}%` }}></div>
            </div>
            <div className="text-sm text-gray-600">
              {getTotalAnswered()} / {questions.length} questions answered
            </div>
          </div>

          {canSubmit() && (
            <button
              onClick={() => onComplete(answers)}
              className="btn-primary w-full mt-4"
            >
              Submit Assessment
            </button>
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">
          <div className="max-w-4xl mx-auto fade-in">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-4 mb-4">
                <span className="text-5xl">{currentPrincipleData?.icon}</span>
                <div>
                  <h1 className="text-4xl font-bold gradient-text">
                    {currentPrincipleData?.name}
                  </h1>
                  <p className="text-xl text-gray-600">
                    {currentPrincipleData?.description}
                  </p>
                </div>
              </div>

              {/* Section Progress */}
              <div className="bg-white p-4 rounded-lg shadow">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold">Section Progress</span>
                  <span className="text-indigo-600 font-bold">
                    {Math.round((getAnsweredCount(currentPrinciple) / currentQuestions.length) * 100)}%
                  </span>
                </div>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${(getAnsweredCount(currentPrinciple) / currentQuestions.length) * 100}%`,
                    }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Questions */}
            <div className="space-y-6">
              {currentQuestions.map((question, index) => {
                const answer = answers.find(a => a.questionId === question.id);
                const isAnswered = answer?.value !== null;

                return (
                  <div
                    key={question.id}
                    className={`question-card ${isAnswered ? 'answered' : ''}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="principle-badge">
                            {question.id}
                          </span>
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              question.criticality === 'core'
                                ? 'bg-red-100 text-red-800'
                                : question.criticality === 'important'
                                ? 'bg-orange-100 text-orange-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}
                          >
                            {question.criticality}
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold mb-2">
                          {question.text}
                        </h3>
                        <p className="text-sm text-gray-500">
                          source: {question.source}
                        </p>
                      </div>
                    </div>

                    {/* Answer Buttons */}
                    <div className="flex gap-3 mb-3">
                      <button
                        className={`answer-btn ${
                          answer?.value === 'yes' ? 'selected yes' : ''
                        }`}
                        onClick={() => handleAnswer(question.id, 'yes')}
                      >
                        Yes
                      </button>
                      <button
                        className={`answer-btn ${
                          answer?.value === 'partially' ? 'selected partially' : ''
                        }`}
                        onClick={() => handleAnswer(question.id, 'partially')}
                      >
                        Partially
                      </button>
                      <button
                        className={`answer-btn ${
                          answer?.value === 'no' ? 'selected no' : ''
                        }`}
                        onClick={() => handleAnswer(question.id, 'no')}
                      >
                        No
                      </button>
                    </div>

                    {/* Add Note Button */}
                    <button
                      onClick={() => handleNote(question.id)}
                      className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 text-sm font-semibold"
                    >
                      <FileText className="w-4 h-4" />
                      {answer?.note ? 'Edit documentation / note' : 'Add documentation / note'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Note Modal */}
      {noteModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full mx-4">
            <h3 className="text-2xl font-bold mb-4">Add Documentation / Note</h3>
            <textarea
              className="w-full h-40 p-4 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
              placeholder="Add any relevant documentation, evidence, or notes..."
              value={tempNote}
              onChange={(e) => setTempNote(e.target.value)}
            ></textarea>
            <div className="flex gap-3 mt-4">
              <button onClick={saveNote} className="btn-primary flex-1">
                Save Note
              </button>
              <button
                onClick={() => setNoteModal({ questionId: '', show: false })}
                className="px-6 py-3 border-2 border-gray-300 rounded-lg font-semibold hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionsPage;
